import { useRef, useCallback, useEffect } from "react";

/** iPhone/iPad: Safari grava AAC/MP4; isTypeSupported('audio/webm') às vezes mente. */
export const isAppleMobileDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ se passa por Mac com touch
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

const PREFERRED_MIME_TYPES_DESKTOP = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

const PREFERRED_MIME_TYPES_APPLE = [
  "audio/mp4",
  "audio/aac",
  "audio/mpeg",
];

export const pickAudioMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }
  const preferred = isAppleMobileDevice()
    ? PREFERRED_MIME_TYPES_APPLE
    : PREFERRED_MIME_TYPES_DESKTOP;
  for (const type of preferred) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return "";
};

export const buildAudioFilename = (mimeType, ticketChannel) => {
  const ts = Date.now();
  const normalized = String(mimeType || "").toLowerCase();

  if (normalized.includes("webm")) return `audio_${ts}.webm`;
  if (normalized.includes("ogg") || normalized.includes("opus")) {
    return `audio_${ts}.ogg`;
  }
  if (
    normalized.includes("mp4") ||
    normalized.includes("m4a") ||
    normalized.includes("aac")
  ) {
    return `audio_${ts}.m4a`;
  }
  if (normalized.includes("mpeg") || normalized.includes("mp3")) {
    return `audio_${ts}.mp3`;
  }
  if (normalized.includes("wav")) return `audio_${ts}.wav`;

  // iPhone: nunca defaultar para .webm (conteúdo real é MP4 → #131053 na Meta).
  if (isAppleMobileDevice()) {
    return `audio_${ts}.m4a`;
  }
  if (["whatsapp", "whatsapp_oficial"].includes(ticketChannel)) {
    return `audio_${ts}.webm`;
  }
  return `${ts}.m4a`;
};

export const resolveAudioFileType = (mimeType) => mimeType || "audio/mpeg";

const detachHandlers = (recorder) => {
  if (!recorder) return;
  recorder.ondataavailable = null;
  recorder.onstop = null;
  recorder.onerror = null;
};

const stopStream = (session) => {
  if (session?.stream) {
    session.stream.getTracks().forEach((track) => track.stop());
    session.stream = null;
  }
};

/**
 * Gravação via MediaRecorder nativo — sem encode MP3 em tempo real na main thread.
 *
 * O MediaRecorder emite o cabeçalho do WebM (EBML + Tracks) apenas no primeiro
 * chunk da sessão. Por isso cada gravação carrega seus próprios chunks: se um
 * gravador antigo ainda estiver drenando eventos, ele não pode zerar nem
 * escrever na lista da gravação nova, senão o arquivo nasce sem cabeçalho e o
 * ffmpeg do backend rejeita com "Invalid data found when processing input".
 */
const useTicketAudioRecorder = () => {
  const sessionRef = useRef(null);

  const discardSession = useCallback((session) => {
    if (!session) return;
    detachHandlers(session.recorder);
    try {
      if (session.recorder && session.recorder.state !== "inactive") {
        session.recorder.stop();
      }
    } catch (_) {}
    session.chunks = [];
    stopStream(session);
  }, []);

  useEffect(() => {
    return () => {
      discardSession(sessionRef.current);
      sessionRef.current = null;
    };
  }, [discardSession]);

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === "undefined") {
      throw new Error("MediaRecorder não suportado neste navegador");
    }

    // Uma sessão anterior que não foi encerrada corretamente contaminaria esta.
    discardSession(sessionRef.current);
    sessionRef.current = null;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });

    const mimeType = pickAudioMimeType();
    let recorder;
    try {
      recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      throw error;
    }

    const session = { recorder, stream, chunks: [] };
    sessionRef.current = session;

    recorder.ondataavailable = (event) => {
      if (sessionRef.current !== session) return;
      if (event.data && event.data.size > 0) {
        session.chunks.push(event.data);
      }
    };

    recorder.onerror = (event) => {
      // eslint-disable-next-line no-console
      console.error("[AudioRecorder] Erro na gravação:", event?.error || event);
    };

    recorder.start(250);
  }, [discardSession]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve, reject) => {
      const session = sessionRef.current;
      const recorder = session?.recorder;

      if (!recorder || recorder.state === "inactive") {
        discardSession(session);
        sessionRef.current = null;
        reject(new Error("Nenhuma gravação ativa"));
        return;
      }

      recorder.onstop = () => {
        try {
          const chunks = session.chunks;
          const fallbackMime = isAppleMobileDevice()
            ? "audio/mp4"
            : "audio/webm";
          const mimeType =
            recorder.mimeType ||
            chunks[0]?.type ||
            pickAudioMimeType() ||
            fallbackMime;
          const blob = new Blob(chunks, { type: mimeType });

          detachHandlers(recorder);
          stopStream(session);
          session.chunks = [];
          if (sessionRef.current === session) {
            sessionRef.current = null;
          }

          if (blob.size < 1000) {
            reject(new Error("Áudio muito curto"));
            return;
          }

          resolve({ blob, mimeType });
        } catch (error) {
          reject(error);
        }
      };

      recorder.stop();
    });
  }, [discardSession]);

  const cancelRecording = useCallback(() => {
    return new Promise((resolve) => {
      const session = sessionRef.current;
      sessionRef.current = null;
      discardSession(session);
      resolve();
    });
  }, [discardSession]);

  return {
    startRecording,
    stopRecording,
    cancelRecording,
    isMediaRecorderSupported: typeof MediaRecorder !== "undefined",
  };
};

export default useTicketAudioRecorder;

